import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { FaTrash, FaChartLine } from 'react-icons/fa';
import toast from 'react-hot-toast';

interface ShortLink {
  id: string;
  short_code: string;
  original_url: string;
  title?: string;
  full_url: string;
  created_at: string;
  click_count?: number;
}

const MyLinksTab: React.FC = () => {
  const [links, setLinks] = useState<ShortLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLink, setSelectedLink] = useState<ShortLink | null>(null);
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    loadLinks();
  }, []);

  const loadLinks = async () => {
    try {
      setLoading(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || '';
      
      const res = await fetch('/api/url/my-links', {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      if (!res.ok) {
        throw new Error('Failed to load links');
      }

      const data = await res.json();
      setLinks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading links:', error);
      toast.error('Failed to load links');
    } finally {
      setLoading(false);
    }
  };

  const deleteLink = async (shortCode: string) => {
    if (!window.confirm('Delete this link? This action cannot be undone.')) {
      return;
    }

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || '';
      
      const res = await fetch(`/api/url/${encodeURIComponent(shortCode)}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      if (!res.ok) {
        throw new Error('Failed to delete link');
      }

      setLinks(links.filter(l => l.short_code !== shortCode));
      if (selectedLink?.short_code === shortCode) {
        setSelectedLink(null);
        setStats(null);
      }
      toast.success('Link deleted');
    } catch (error) {
      console.error('Error deleting link:', error);
      toast.error('Failed to delete link');
    }
  };

  const viewStats = async (link: ShortLink) => {
    setSelectedLink(link);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || '';
      
      const res = await fetch(`/api/url/stats/${encodeURIComponent(link.short_code)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      if (!res.ok) {
        throw new Error('Failed to load stats');
      }

      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load analytics');
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading your links...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Info Box */}
      <div className="rounded-xl bg-green-50 p-5 shadow-sm ring-1 ring-green-100">
        <h3 className="mb-2 text-base font-semibold text-green-900">Your Short Links</h3>
        <p className="text-sm text-green-800">
          Manage all your shortened URLs and QR codes. View analytics, copy links, or delete old campaigns.
        </p>
      </div>

      {links.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <div className="mb-4 text-4xl">🔗</div>
          <h3 className="text-lg font-semibold text-gray-900">No Links Yet</h3>
          <p className="mt-2 text-sm text-gray-600">
            Create your first short link or QR code using the other tabs
          </p>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Links List */}
          <div className="lg:col-span-1">
            <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
              <h4 className="mb-4 text-base font-semibold text-gray-900">
                All Links ({links.length})
              </h4>
              
              <div className="space-y-2">
                {links.map((link) => (
                  <div
                    key={link.id}
                    className={`cursor-pointer rounded-lg border p-3 transition-all ${
                      selectedLink?.id === link.id
                        ? 'border-primary bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => viewStats(link)}
                  >
                    <div className="mb-1 text-sm font-medium text-gray-900">
                      {link.title || 'Untitled'}
                    </div>
                    <div className="mb-1 text-xs text-blue-600">
                      {link.full_url.split('/').pop()}
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{new Date(link.created_at).toLocaleDateString()}</span>
                      {link.click_count !== undefined && (
                        <span className="font-medium">{link.click_count} clicks</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-2">
            {selectedLink ? (
              <div className="space-y-4">
                {/* Link Info */}
                <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
                  <h4 className="mb-4 text-base font-semibold text-gray-900">Link Details</h4>
                  
                  <div className="space-y-3">
                    <div>
                      <div className="text-xs font-medium text-gray-600">Title</div>
                      <div className="text-sm text-gray-900">{selectedLink.title || 'Untitled'}</div>
                    </div>
                    
                    <div>
                      <div className="text-xs font-medium text-gray-600">Short URL</div>
                      <div className="flex items-center gap-2">
                        <a
                          href={selectedLink.full_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {selectedLink.full_url}
                        </a>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(selectedLink.full_url);
                            toast.success('Copied to clipboard');
                          }}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          <span className="text-xs">📋</span>
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-xs font-medium text-gray-600">Original URL</div>
                      <div className="text-sm text-gray-900 break-all">{selectedLink.original_url}</div>
                    </div>
                    
                    <div>
                      <div className="text-xs font-medium text-gray-600">Created</div>
                      <div className="text-sm text-gray-900">
                        {new Date(selectedLink.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => deleteLink(selectedLink.short_code)}
                      className="flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>

                {/* Analytics Summary */}
                {stats && (
                  <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
                    <h4 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
                      <FaChartLine /> Analytics Summary
                    </h4>
                    
                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="rounded-lg bg-blue-50 p-4">
                        <div className="text-xs font-medium text-blue-700">Total Clicks</div>
                        <div className="mt-1 text-2xl font-bold text-blue-900">
                          {stats.total_clicks || 0}
                        </div>
                      </div>
                      
                      <div className="rounded-lg bg-green-50 p-4">
                        <div className="text-xs font-medium text-green-700">Last 7 Days</div>
                        <div className="mt-1 text-2xl font-bold text-green-900">
                          {stats.daily_clicks?.slice(-7).reduce((sum: number, day: any) => sum + day.clicks, 0) || 0}
                        </div>
                      </div>
                      
                      <div className="rounded-lg bg-purple-50 p-4">
                        <div className="text-xs font-medium text-purple-700">Unique Referrers</div>
                        <div className="mt-1 text-2xl font-bold text-purple-900">
                          {stats.top_referrers?.length || 0}
                        </div>
                      </div>
                    </div>

                    {stats.last_clicked_at && (
                      <div className="mt-4 text-xs text-gray-600">
                        Last clicked: {new Date(stats.last_clicked_at).toLocaleString()}
                      </div>
                    )}

                    <div className="mt-4">
                      <p className="text-xs text-gray-600">
                        For detailed analytics, create a new link from the URL Shortener tab
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center rounded-xl bg-gray-50 p-12 text-center ring-1 ring-gray-200">
                <div>
                  <div className="mb-3 text-4xl">📊</div>
                  <p className="text-sm text-gray-500">Select a link to view details and analytics</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyLinksTab;
